//@ts-check
import useFormContext from '../context/useFormContext';
import fieldsHelper from '../utils/fieldsHelper';
import contextBinding from './contextBinding';
import repeatingBinding from './repeatingBinding';
import helpers from './helpers';
import languages from '../languages';
/**
 * Find binding related with a specific repeating field
 * @param {import('../logic/types').EcrionFormRepeatingField} field
 * @param {import('../logic/types').EcrionFormState} state
 * @return {import('./types').Binding}
 */

var findRepeatingBinding = function findRepeatingBinding(field, state) {
  var xpath = field.xpath;
  var dataSource = helpers.getFieldDataSource(field, state);
  var formBindings = state.formBindings;
  var bindings = formBindings.bindings;
  var binding = null;
  Object.keys(bindings).forEach(function (bindingId) {
    var fieldId = field.originalId || field.id;
    if (bindings[bindingId].dataSource === dataSource && bindings[bindingId].xpath === xpath && bindings[bindingId].parentFormId === fieldId) binding = bindings[bindingId];
  });
  return binding;
};
/**
 * Find binding related with a specified field
 * @param {import('../logic/types').EcrionFormField} field
 * @param {import('../logic/types').EcrionFormState} state
 * @returns {import('./types').Binding}
 */


var findFieldBinding = function findFieldBinding(field, state) {
  if (field.type === 'repeating') return findRepeatingBinding(field, state);
  var bindingId = state.formBindings.fieldsBindingMap[field.originalId || field.id];
  return state.formBindings.bindings[bindingId];
};
/**
 *
 * @param {import('./types').BindingParamDefinition} param
 * @param {import('./types').Binding} binding
 * @param {import('../logic/types').EcrionFormState} state
 * @returns {boolean}
 */


var canBeUsedAsBindingParameter = function canBeUsedAsBindingParameter(param, binding, state) {
  if (param.dataSource !== binding.dataSource) return false;
  if (param.parentFormId === binding.parentFormId) return true;
  return fieldsHelper.fieldHasAncestor(state, binding.parentFormId, param.parentFormId);
};
/**
 *
 * @param {import('../logic/types').EcrionFormField} field
 * @param {import('../logic/types').EcrionFormState} state
 * @param {import('./types').Binding} binding
 * @returns {Array<import('./types').BindingParam>}
 */


var getBindingParams = function getBindingParams(field, state, binding) {
  /** @type {Array<import('./types').BindingParam>} */
  var params = [];
  var bindingParams = state.formBindings.bindingParams;
  bindingParams.forEach(function (param) {
    if (!canBeUsedAsBindingParameter(param, binding, state)) return;
    var fieldParamSuffix = fieldsHelper.findFieldSuffixByContext(state, state.fields[param.fieldId], field);
    params.push({
      name: param.name,
      fieldId: param.fieldId,
      fieldIdWithSuffix: param.fieldId + fieldParamSuffix,
      value: (state.data[param.fieldId + fieldParamSuffix] || '') + ''
    });
  });
  return params;
};
/**
 * Get binding data for a field
 * @param {import('../logic/types').EcrionFormField} field
 * @param {import('../logic/types').EcrionFormState} state
 * @return {import('./types').useBindingHookDataType}
 */


var getBindingData = function getBindingData(field, state) {
  var binding = findFieldBinding(field, state);
  if (!binding) return null;
  var params = getBindingParams(field, state, binding);
  var children = [];

  if (field.type === 'repeating') {
    field.children.forEach(function (childId) {
      var childField = state.fields[childId];
      if (!childField || childField.type !== 'repeating') return;
      var childBinding = getBindingData(childField, state);
      if (!childBinding) return;
      children.push(childBinding);
    });
  }

  var translatedProperties = languages.translate(state, field);
  return {
    dataSource: binding.dataSource,
    xpath: binding.xpath,
    binding: binding,
    fieldId: field.originalId || field.id,
    fieldsBinding: {
      fields: binding.fields,
      params: params,
      context: binding.context
    },
    allowMultiLookupSelection: field.type === 'repeating',
    searchLabel: field.type === 'repeating' ? translatedProperties['caption'] || field['caption'] : '',
    children: children
  };
};
/**
 * @param {string} fieldId
 * @returns {import('./types').useBindingHookResultType}
 */


var useBinding = function useBinding(fieldId) {
  var _useFormContext = useFormContext(),
      state = _useFormContext.state,
      dispatch = _useFormContext.dispatch;

  if (!state.formBindings || !state.formBindings.bindings) return null;
  var field = state.fields[fieldId];
  var data = getBindingData(field, state);
  if (!data) return;
  /** @param {Array<import('./types').ApplyLookupValueType>} applyValues */

  var applyForSimpleBinding = function applyForSimpleBinding(applyValues) {
    var lookupValuesKeys = Object.keys(applyValues[0]);
    var lookupValues = applyValues[0][lookupValuesKeys[0]];
    var values = lookupValues.values,
        context = lookupValues.context;
    var fieldValues = {};
    var fieldSufix = fieldsHelper.findContextSuffix(state.fields[fieldId]);
    data.binding.fields.map(function (val, index) {
      var value = values[index];
      var id = val.fieldId;

      if (fieldSufix && field.parentId == state.fields[id + fieldSufix].parentId) {
        fieldValues[id + fieldSufix] = value;
      } else {
        fieldValues[id] = value;
      }
    });
    contextBinding.linkContext(fieldId, context, data.fieldsBinding.params, state, dispatch);
    dispatch({
      type: 'SET_FIELDS_VALUES',
      payload: fieldValues
    });
  };
  /** @param {Array<import('./types').ApplyLookupValueType>} applyValues */


  var apply = function apply(applyValues) {
    if (field.type !== 'repeating') applyForSimpleBinding(applyValues);else repeatingBinding.apply(fieldId, data, applyValues, state, dispatch);
  };

  return {
    data: data,
    apply: apply
  };
};

export default useBinding;